import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import AdminSidebar from 'admin/sidebar/AdminSidebar';
import axios from 'axios';
import './app.css';

const fmtAED = (n) => {
   const v = Number(n || 0);
   return `${v < 0 ? '− ' : ''}AED ${Math.abs(v).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
};
const negStyle = (n) => (Number(n) < 0 ? { color: '#c0392b' } : undefined);
const fmtNum = (n) => Number(n || 0).toLocaleString('en-US');

const API_BASE = process.env.REACT_APP_API_URL || 'https://backend.washta.com';
const DASHBOARD_V2_URL = `${API_BASE}/api/admin/v2/dashboard`;

// Helper to get date range for API.
// Days start at the viewer's local midnight (same as CRM → Packages), sent as full ISO timestamps —
// a bare UTC date ("2026-10-14") would still be yesterday for the first hours of the local day.
const getDateRangeForFilter = (range) => {
   const end = new Date();
   const start = new Date(end);
   start.setHours(0, 0, 0, 0);

   switch (range) {
      case 'This Week':
         start.setDate(start.getDate() - 6);
         break;
      case 'This Month':
         start.setDate(start.getDate() - 29);
         break;
      case 'All time':
         start.setFullYear(2020, 0, 1);
         break;
      default: // Today
         break;
   }

   return {
      startDate: encodeURIComponent(start.toISOString()),
      endDate: encodeURIComponent(end.toISOString()),
   };
};

const API_EMPTY_RANGE = {
   kpis: { grossGMV: 0, commissionEarned: 0, platformFees: 0, stripeFees: 0, promoDiscounts: 0, washtaNet: 0 },
   providers: [],
   topLocations: [],
   serviceMix: [],
   carTypeSplit: [],
   addOns: [],
   promoCodes: [],
   split: null,
   platformTotal: { orders: 0, shopOrders: 0, residentialOrders: 0, grossGMV: 0, promoAbsorbed: 0, platformFees: 0, stripeFees: 0, commissionEarned: 0, washtaNet: 0, providerEntitlement: 0 },
};

const EMPTY_SPLIT_ROW = { orders: 0, gmv: 0, promo: 0, commission: 0, platformFee: 0, stripeFee: 0, charged: 0, providerEntitlement: 0, washtaNet: 0 };

// Money split of one order type (shop / residential) as returned by the API
const transformSplitRow = (row = {}) => Object.keys(EMPTY_SPLIT_ROW).reduce(
   (acc, key) => ({ ...acc, [key]: numberFrom(row[key]) }),
   {}
);

// A provider row limited to one order type (shop or residential), using that type's money split
const providerRowFor = (p, s) => ({
   ...p,
   orders: s.orders,
   grossGMV: s.gmv,
   promoAbsorbed: s.promo,
   platformFees: s.platformFee,
   stripeFees: s.stripeFee,
   commission: s.commission,
   commissionEarned: s.commission,
   washtaNet: s.washtaNet,
   providerEntitlement: s.providerEntitlement,
});

const EMPTY_PROVIDER_TOTAL = { orders: 0, grossGMV: 0, promoAbsorbed: 0, platformFees: 0, stripeFees: 0, commissionEarned: 0, washtaNet: 0, providerEntitlement: 0 };

const sumProviderRows = (rows) => rows.reduce(
   (t, p) => Object.keys(t).reduce((acc, key) => ({ ...acc, [key]: t[key] + (p[key] || 0) }), {}),
   { ...EMPTY_PROVIDER_TOTAL }
);

const ProviderGmvChart = ({ title, rows }) => {
   const max = Math.max(1, ...rows.map((p) => p.grossGMV || 1));
   return (
      <div className="ws-chart-wrap">
         <div className="ws-chart-head">
            <div className="ws-chart-title">{title}</div>
            <div className="ws-chart-legend">
               <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <div className="ws-legend-dot" style={{ background: '#6B5FE4' }}></div>
                  <span className="ws-legend-label">GMV</span>
               </div>
            </div>
         </div>
         <div className="ws-chart-body">
            {rows.map((p, i) => (
               <div className="ws-bar-row" key={i}>
                  <span className="ws-bar-label">{p.name}</span>
                  <div className="ws-bar-track">
                     <div className="ws-bar-fill" style={{ width: `${Math.max(2, (p.grossGMV / max) * 100)}%`, background: '#6B5FE4' }}></div>
                  </div>
                  <span className="ws-bar-val">{fmtAED(p.grossGMV)}</span>
               </div>
            ))}
         </div>
      </div>
   );
};

// showPromo=false drops the Promo Absorbed column (packages have no promo)
const ProviderBreakdownTable = ({ rows, total, totalLabel, showPromo = true }) => (
   <div className="ws-table-scroll">
      <table className="ws-tbl" style={{ minWidth: 1100 }}>
         <thead>
            <tr>
               <th>Provider</th><th>Commission</th><th className="ws-r">Orders</th>
               <th className="ws-r">Gross GMV</th>{showPromo && <th className="ws-r">Promo Absorbed</th>}
               <th className="ws-r">Platform Fees</th><th className="ws-r">Stripe Fees</th>
               <th className="ws-r">Commission Earned</th><th className="ws-r">Washta Net</th>
               <th className="ws-r">Provider Net</th>
            </tr>
         </thead>
         <tbody>
            {rows.map((p, i) => (
               <tr key={i}>
                  <td>
                     <div style={{ fontWeight: 700 }}>{p.name}</div>
                     <div style={{ fontSize: 9, color: '#aaa', marginTop: 2 }}>{p.sub}</div>
                  </td>
                  <td>
                     {p.commissionPct != null
                        ? <span className="ws-commission-tag">{p.commissionPct}%</span>
                        : <span className="ws-pill ws-p-expired">TBD</span>}
                  </td>
                  <td className="ws-r">{p.orders ?? <span className="ws-dash">—</span>}</td>
                  <td className="ws-r">{formatMaybeAED(p.grossGMV)}</td>
                  {showPromo && <td className="ws-r">{formatMaybeAED(p.promoAbsorbed)}</td>}
                  <td className="ws-r">{formatMaybeAED(p.platformFees)}</td>
                  <td className="ws-r">{formatMaybeAED(p.stripeFees)}</td>
                  <td className="ws-r">{formatMaybeAED(p.commissionEarned)}</td>
                  <td className="ws-r" style={{ fontWeight: 700, ...negStyle(p.washtaNet) }}>{formatMaybeAED(p.washtaNet)}</td>
                  <td className="ws-r" style={{ fontWeight: 700 }}>{formatMaybeAED(p.providerEntitlement)}</td>
               </tr>
            ))}
            {rows.length === 0 && (
               <tr><td colSpan={showPromo ? 10 : 9} className="ws-muted" style={{ textAlign: 'center', padding: 16 }}>No orders in this period.</td></tr>
            )}
         </tbody>
         <tfoot>
            <tr>
               <td colSpan={2}>{totalLabel}</td>
               <td className="ws-r">{total.orders}</td>
               <td className="ws-r">{fmtAED(total.grossGMV)}</td>
               {showPromo && <td className="ws-r">− {fmtAED(total.promoAbsorbed)}</td>}
               <td className="ws-r">+ {fmtAED(total.platformFees)}</td>
               <td className="ws-r">− {fmtAED(total.stripeFees)}</td>
               <td className="ws-r">{fmtAED(total.commissionEarned)}</td>
               <td className="ws-r" style={negStyle(total.washtaNet)}>{fmtAED(total.washtaNet)}</td>
               <td className="ws-r">{fmtAED(total.providerEntitlement)}</td>
            </tr>
         </tfoot>
      </table>
   </div>
);

const OrderTypeTag = ({ type }) => (
   <span className="ws-pill" style={type === 'residential'
      ? { background: '#fff4dc', color: '#92400E', marginLeft: 6 }
      : { background: '#f0eeff', color: '#4a3db8', marginLeft: 6 }}>
      {type === 'residential' ? 'Residential' : 'Shop'}
   </span>
);

const EMPTY_HEALTH = {
   activeCustomers: 0,
   activeCustomersDelta: 'No customer data returned by API',
   atRiskCustomers: 0,
   atRiskNote: 'No customer data returned by API',
   gmvTrendPct: '—',
   coverage: '0 / 0',
   coverageNote: 'No location data returned by API',
   completionRate: '0%',
   cancellationRate: '0%',
   totalOrders: 0,
   totalGMV: 0,
};

const numberFrom = (value) => {
   if (typeof value === 'number') return value;
   if (typeof value === 'string') {
      const parsed = Number(value.replace(/[^0-9.-]/g, ''));
      return Number.isFinite(parsed) ? parsed : 0;
   }
   if (value && typeof value === 'object') {
      if (typeof value.value === 'number') return value.value;
      if (typeof value.value === 'string') {
         const parsed = Number(value.value.replace(/[^0-9.-]/g, ''));
         return Number.isFinite(parsed) ? parsed : 0;
      }
   }
   return Number(value || 0);
};

const percentFrom = (value) => {
   if (typeof value === 'number') return value;
   if (typeof value === 'string') return Number(value.replace('%', '')) || 0;
   return 0;
};

const formatMaybeAED = (value) => (value == null ? '—' : fmtAED(value));

const transformProviderData = (providers = []) => providers.map((provider) => {
   const gmv = numberFrom(provider.totalGMV);
   const commission = numberFrom(provider.commission);
   const orders = provider.totalOrders || 0;
   // Fees, net and entitlement come from the API so rows add up to the dashboard totals
   const platformFees = numberFrom(provider.platformFees);
   const stripeFees = numberFrom(provider.stripeFees);
   const washtaNet = numberFrom(provider.washtaNet);
   const providerEntitlement = numberFrom(provider.providerEntitlement);
   const promoAbsorbed = numberFrom(provider.promoAbsorbed ?? provider.promoDiscount);
   return {
      name: provider.providerName || provider.shopNames?.[0] || provider.shopName || provider.business?.companyName || provider.providerEmail || 'Provider',
      _id: provider._id,
      email: provider.providerEmail || provider.email,
      status: provider.status || 'active',
      sub: (provider.shopNames && provider.shopNames.length > 0 ? provider.shopNames.filter(Boolean).join(', ') : null) || provider.providerEmail || provider.email || '—',
      commissionPct: 8,
      orders,
      shopOrders: provider.shopOrders ?? orders,
      residentialOrders: provider.residentialOrders || 0,
      shopSplit: transformSplitRow(provider.split?.shop),
      residentialSplit: transformSplitRow(provider.split?.residential),
      grossGMV: gmv,
      commission,
      commissionEarned: commission,
      averageOrderValue: numberFrom(provider.averageOrderValue),
      promoAbsorbed,
      platformFees,
      stripeFees,
      washtaNet,
      providerEntitlement,
   };
});

const transformLocationData = (locations = []) => locations.map((location) => ({
   shop: location.shopName || location.providerName || 'Washta',
   location: location.locationName || location.name || location.city || 'Unknown location',
   orders: location.totalOrders || 0,
   gmv: numberFrom(location.totalGMV),
   orderType: location.orderType || 'shop',
   _id: location._id,
}));

const transformServiceMix = (services = []) => services.map((service, index) => ({
   name: service.serviceName || service.category || 'Other',
   pct: percentFrom(service.percentageOfOrders),
   color: ['#6B5FE4', '#4a3db8', '#d8d2ff', '#1a6641', '#92400E'][index % 5],
}));

const transformCarTypeSplit = (carTypes = []) => carTypes.map((carType, index) => ({
   name: carType.carType || 'Unknown',
   pct: percentFrom(carType.percentageOfOrders),
   color: ['#92400E', '#6B5FE4', '#1a6641', '#888'][index % 4],
}));

const transformAddOns = (addons = []) => {
   const totalUsage = addons.reduce((sum, addon) => sum + numberFrom(addon.totalUsage), 0);
   return addons.map((addon) => ({
      name: addon.name || 'Add-on',
      pct: totalUsage > 0 ? Math.round((numberFrom(addon.totalUsage) / totalUsage) * 100) : 0,
   }));
};

const transformPromoData = (promos = []) => promos.map((promo) => ({
   code: promo.promoCode || 'Unknown',
   type: promo.discountType || 'Promo',
   usageCount: promo.usageCount || 0,
   totalDiscount: numberFrom(promo.totalDiscount),
   averageDiscount: numberFrom(promo.averageDiscount),
   totalGMV: numberFrom(promo.totalGMV),
}));

const buildEmptyRangeData = () => ({ ...API_EMPTY_RANGE });

// ── Skeleton loader — mirrors the real dashboard layout ─────────────────
const SalesSkeleton = () => (
   <div className="ws-view-block" style={{ padding: 0 ,width:'100%'}}>
      <div className="ws-sec-head" style={{ alignItems: 'flex-start', marginBottom: 16 }}>
         <div>
            <div className="ws-skel ws-skel-title"></div>
            <div className="ws-skel ws-skel-sub"></div>
         </div>
         <div style={{ display: 'flex', gap: 6 }}>
            {Array.from(new Array(4)).map((_, i) => <div key={i} className="ws-skel ws-skel-pill"></div>)}
         </div>
      </div>

      <div className="ws-health-strip">
         {Array.from(new Array(4)).map((_, i) => (
            <div className="ws-health-card" key={i}>
               <div className="ws-skel ws-skel-icon"></div>
               <div style={{ flex: 1 }}>
                  <div className="ws-skel ws-skel-line-sm"></div>
                  <div className="ws-skel ws-skel-line-md" style={{ marginTop: 6 }}></div>
                  <div className="ws-skel ws-skel-line-sm" style={{ marginTop: 6, width: '70%' }}></div>
               </div>
            </div>
         ))}
      </div>

      <div className="ws-kpi-strip ws-kpi-6">
         {Array.from(new Array(6)).map((_, i) => (
            <div className="ws-kpi-card" key={i}>
               <div className="ws-skel ws-skel-line-sm"></div>
               <div className="ws-skel ws-skel-line-lg" style={{ marginTop: 8 }}></div>
               <div className="ws-skel ws-skel-line-sm" style={{ marginTop: 8, width: '80%' }}></div>
            </div>
         ))}
      </div>

      <div className="ws-skel ws-skel-block" style={{ height: 150, marginBottom: 16 }}></div>
      <div className="ws-skel ws-skel-block" style={{ height: 180, marginBottom: 16 }}></div>
      <div className="ws-skel ws-skel-block" style={{ height: 220 }}></div>
   </div>
);

const AdminSales = () => {
   const navigate = useNavigate();
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState(null);
   const [topOpen, setTopOpen] = useState(true);
   const [promoOpen, setPromoOpen] = useState(true);
   const [range, setRange] = useState('Today');
   const [rangeData, setRangeData] = useState(buildEmptyRangeData());
   const [health, setHealth] = useState(EMPTY_HEALTH);

   useEffect(() => {
      window.scrollTo(0, 0);
   }, []);

   // Fetch dashboard data from backend APIs
   useEffect(() => {
      const fetchDashboardData = async () => {
         try {
            setLoading(true);
            setError(null);
            
            const token = localStorage.getItem('accessTokenAdmin');
            if (!token) {
               setError('Not authenticated');
               return;
            }
            
            const headers = { Authorization: `Bearer ${token}` };
            const { startDate, endDate } = getDateRangeForFilter(range);
            
            // Fetch multiple endpoints in parallel
            const [salesRes, providersRes, locationsRes, servicesRes, carTypesRes, addonsRes, promosRes, customersRes, healthRes] = await Promise.all([
               axios.get(`${DASHBOARD_V2_URL}/sales?startDate=${startDate}&endDate=${endDate}`, { headers }).catch(() => null),
               axios.get(`${DASHBOARD_V2_URL}/providers?startDate=${startDate}&endDate=${endDate}`, { headers }).catch(() => null),
               axios.get(`${DASHBOARD_V2_URL}/locations?startDate=${startDate}&endDate=${endDate}`, { headers }).catch(() => null),
               axios.get(`${DASHBOARD_V2_URL}/services?startDate=${startDate}&endDate=${endDate}`, { headers }).catch(() => null),
               axios.get(`${DASHBOARD_V2_URL}/car-types?startDate=${startDate}&endDate=${endDate}`, { headers }).catch(() => null),
               axios.get(`${DASHBOARD_V2_URL}/addons?startDate=${startDate}&endDate=${endDate}`, { headers }).catch(() => null),
               axios.get(`${DASHBOARD_V2_URL}/promos?startDate=${startDate}&endDate=${endDate}`, { headers }).catch(() => null),
               axios.get(`${DASHBOARD_V2_URL}/customers?startDate=${startDate}&endDate=${endDate}`, { headers }).catch(() => null),
               axios.get(`${DASHBOARD_V2_URL}/health?startDate=${startDate}&endDate=${endDate}`, { headers }).catch(() => null),
            ]);
            
            const salesKpis = salesRes?.data?.data?.kpis || {};
            const providerSummary = providersRes?.data?.data?.summary || {};
            const locationSummary = locationsRes?.data?.data?.summary || {};
            const customerKpis = customersRes?.data?.data?.kpis || {};
            const healthData = healthRes?.data?.data || {};
            const healthMetrics = healthData.metrics || {};
            const healthHealth = healthData.health || {};

            const currentKpis = {
               grossGMV: numberFrom(salesKpis.grossGMV),
               commissionEarned: numberFrom(salesKpis.commissionEarned),
               platformFees: numberFrom(salesKpis.platformFees),
               stripeFees: numberFrom(salesKpis.stripeFees),
               promoDiscounts: numberFrom(salesKpis.promoDiscounts),
               washtaNet: numberFrom(salesKpis.washtaNet),
            };

            const providerRows = transformProviderData(providersRes?.data?.data?.providers || []);
            const locationRows = transformLocationData(locationsRes?.data?.data?.locations || []);
            const serviceRows = transformServiceMix(servicesRes?.data?.data?.services || []);
            const carTypeRows = transformCarTypeSplit(carTypesRes?.data?.data?.carTypes || []);
            const addonRows = transformAddOns(addonsRes?.data?.data?.addons || []);
            const promoRows = transformPromoData(promosRes?.data?.data?.promos || []);

            const totalOrders = healthMetrics.totalOrders || numberFrom(providerSummary.totalOrders) || 0;
            const totalGMV = numberFrom(healthMetrics.totalGMV) || numberFrom(salesKpis.grossGMV) || 0;
            const completionRate = healthHealth.completionRate || '0%';
            const cancellationRate = healthHealth.cancellationRate || '0%';

            const nextRangeData = {
               kpis: currentKpis,
               providers: providerRows,
               topLocations: locationRows.slice(0, 3),
               serviceMix: serviceRows,
               carTypeSplit: carTypeRows,
               addOns: addonRows,
               promoCodes: promoRows,
               // Shop (one-off) vs residential (monthly package) split for the selected range
               split: salesRes?.data?.data?.split
                  ? {
                     shop: transformSplitRow(salesRes.data.data.split.shop),
                     residential: transformSplitRow(salesRes.data.data.split.residential),
                     total: transformSplitRow(salesRes.data.data.split.total),
                  }
                  : null,
               // Totals are the sum of the rows above, so the footer always matches the table
               platformTotal: providerRows.reduce((t, p) => ({
                  orders: t.orders + (p.orders || 0),
                  shopOrders: t.shopOrders + (p.shopOrders || 0),
                  residentialOrders: t.residentialOrders + (p.residentialOrders || 0),
                  grossGMV: t.grossGMV + p.grossGMV,
                  promoAbsorbed: t.promoAbsorbed + p.promoAbsorbed,
                  platformFees: t.platformFees + p.platformFees,
                  stripeFees: t.stripeFees + p.stripeFees,
                  commissionEarned: t.commissionEarned + p.commissionEarned,
                  washtaNet: t.washtaNet + p.washtaNet,
                  providerEntitlement: t.providerEntitlement + p.providerEntitlement,
               }), { orders: 0, shopOrders: 0, residentialOrders: 0, grossGMV: 0, promoAbsorbed: 0, platformFees: 0, stripeFees: 0, commissionEarned: 0, washtaNet: 0, providerEntitlement: 0 }),
            };

            const nextHealth = {
               activeCustomers: numberFrom(customerKpis.activeCustomers),
               activeCustomersDelta: customerKpis.activeCustomers?.percentage
                  ? `${customerKpis.activeCustomers.percentage} of total customers active`
                  : `${numberFrom(customerKpis.activeCustomers)} active in period`,
               atRiskCustomers: numberFrom(customerKpis.atRiskCustomers),
               atRiskNote: customerKpis.atRiskCustomers?.percentage
                  ? `${customerKpis.atRiskCustomers.percentage} of total customers at risk`
                  : `${numberFrom(customerKpis.atRiskCustomers)} at risk`,
               gmvTrendPct: completionRate,
               coverage: `${providerRows.length} providers · ${locationRows.length} locations`,
               coverageNote: `Cancellation rate: ${cancellationRate}`,
               completionRate,
               cancellationRate,
               totalOrders,
               totalGMV,
            };
            
            setRangeData(nextRangeData);
            setHealth(nextHealth);
            
         } catch (err) {
            console.error('Error fetching dashboard data:', err);
            setError(err.response?.data?.message || 'Failed to load dashboard data');
         } finally {
            setLoading(false);
         }
      };
      
      fetchDashboardData();
   }, [range]);

   // Shop orders and residential packages are shown in separate provider tables
   const providerTables = useMemo(() => {
      const shopRows = rangeData.providers
         .filter((p) => p.shopOrders > 0)
         .map((p) => providerRowFor(p, p.shopSplit))
         .sort((a, b) => b.orders - a.orders);
      const residentialRows = rangeData.providers
         .filter((p) => p.residentialOrders > 0)
         .map((p) => providerRowFor(p, p.residentialSplit))
         .sort((a, b) => b.orders - a.orders);
      return {
         shopRows,
         shopTotal: sumProviderRows(shopRows),
         residentialRows,
         residentialTotal: sumProviderRows(residentialRows),
      };
   }, [rangeData]);

   const promoSummary = useMemo(() => {
      const totalUsage = rangeData.promoCodes.reduce((sum, promo) => sum + numberFrom(promo.usageCount), 0);
      const totalDiscount = rangeData.promoCodes.reduce((sum, promo) => sum + numberFrom(promo.totalDiscount), 0);
      return { totalCount: rangeData.promoCodes.length, totalUsage, totalDiscount };
   }, [rangeData]);

   const exportCSV = () => {
      const rows = [];

      // ── Section 1: Summary KPIs ──
      rows.push(['SALES DASHBOARD EXPORT', `Period: ${range}`, `Generated: ${new Date().toLocaleString()}`]);
      rows.push([]);
      rows.push(['KPI SUMMARY']);
      rows.push(['Metric', 'Value']);
      rows.push(['Gross GMV', `AED ${rangeData.kpis.grossGMV.toFixed(2)}`]);
      rows.push(['Commission Earned (8%)', `AED ${rangeData.kpis.commissionEarned.toFixed(2)}`]);
      rows.push(['Platform Fees', `AED ${rangeData.kpis.platformFees.toFixed(2)}`]);
      rows.push(['Stripe Fees', `AED ${rangeData.kpis.stripeFees.toFixed(2)}`]);
      rows.push(['Promo Discounts', `AED ${rangeData.kpis.promoDiscounts.toFixed(2)}`]);
      rows.push(['Washta Net', `AED ${rangeData.kpis.washtaNet.toFixed(2)}`]);
      rows.push(['Total Orders', rangeData.platformTotal.orders]);
      rows.push(['Active Customers', health.activeCustomers]);
      rows.push(['At-Risk Customers', health.atRiskCustomers]);
      rows.push(['Completion Rate', health.completionRate]);
      rows.push(['Cancellation Rate', health.cancellationRate]);
      rows.push([]);

      // ── Section 2: Provider Breakdown ──
      // Shop orders and residential packages are exported as separate tables, same as on screen
      [
         ['PROVIDER BREAKDOWN · SHOP ORDERS', providerTables.shopRows, providerTables.shopTotal, 'SHOP ORDERS TOTAL', true],
         ['PROVIDER BREAKDOWN · RESIDENTIAL PACKAGES', providerTables.residentialRows, providerTables.residentialTotal, 'PACKAGES TOTAL', false],
      ].forEach(([title, list, total, totalLabel, withPromo]) => {
         // packages have no promo, so their table has no Promo Absorbed column
         const promo = (v) => (withPromo ? [v] : []);
         rows.push([title]);
         rows.push(['Provider', 'Shop(s)', 'Commission %', 'Orders', 'Gross GMV (AED)', ...promo('Promo Absorbed (AED)'), 'Platform Fees (AED)', 'Stripe Fees (AED)', 'Commission Earned (AED)', 'Washta Net (AED)', 'Provider Net (AED)', 'Status']);
         list.forEach((p) => {
            rows.push([
               p.name, p.sub, `${p.commissionPct ?? 8}%`, p.orders,
               p.grossGMV.toFixed(2), ...promo(p.promoAbsorbed.toFixed(2)),
               p.platformFees.toFixed(2), p.stripeFees.toFixed(2), p.commissionEarned.toFixed(2),
               p.washtaNet.toFixed(2), p.providerEntitlement.toFixed(2),
               p.status || 'active',
            ]);
         });
         rows.push([totalLabel, '', '', total.orders,
            total.grossGMV.toFixed(2), ...promo(total.promoAbsorbed.toFixed(2)),
            total.platformFees.toFixed(2), total.stripeFees.toFixed(2), total.commissionEarned.toFixed(2),
            total.washtaNet.toFixed(2), total.providerEntitlement.toFixed(2), '']);
         rows.push([]);
      });

      // ── Section 3: Top Locations ──
      if (rangeData.topLocations.length > 0) {
         rows.push(['TOP LOCATIONS']);
         rows.push(['Rank', 'Shop', 'Location', 'Orders', 'GMV (AED)']);
         rangeData.topLocations.forEach((l, i) => {
            rows.push([i + 1, l.shop, l.location, l.orders, l.gmv.toFixed(2)]);
         });
         rows.push([]);
      }

      // ── Section 4: Service Mix ──
      if (rangeData.serviceMix.length > 0) {
         rows.push(['SERVICE MIX']);
         rows.push(['Service', '% of Orders']);
         rangeData.serviceMix.forEach((s) => rows.push([s.name, `${s.pct}%`]));
         rows.push([]);
      }

      // ── Section 5: Car Type Split ──
      if (rangeData.carTypeSplit.length > 0) {
         rows.push(['CAR TYPE SPLIT']);
         rows.push(['Car Type', '% of Orders']);
         rangeData.carTypeSplit.forEach((c) => rows.push([c.name, `${c.pct}%`]));
         rows.push([]);
      }

      // ── Section 6: Add-on Performance ──
      if (rangeData.addOns.length > 0) {
         rows.push(['ADD-ON PERFORMANCE']);
         rows.push(['Add-on', '% of Usage']);
         rangeData.addOns.forEach((a) => rows.push([a.name, `${a.pct}%`]));
         rows.push([]);
      }

      // ── Section 7: Promo Codes ──
      if (rangeData.promoCodes.length > 0) {
         rows.push(['PROMO CODE PERFORMANCE']);
         rows.push(['Code', 'Type', 'Usage Count', 'Total Discount (AED)', 'Avg Discount (AED)', 'Total GMV (AED)']);
         rangeData.promoCodes.forEach((p) => {
            rows.push([p.code, p.type, p.usageCount,
               numberFrom(p.totalDiscount).toFixed(2),
               numberFrom(p.averageDiscount).toFixed(2),
               numberFrom(p.totalGMV).toFixed(2)]);
         });
      }

      const csv = rows.map((row) => row.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `washta-sales-${range.replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
   };

   const rankClass = (i) => (i === 0 ? 'ws-rd1' : i === 1 ? 'ws-rd2' : 'ws-rd3');

   // "Shop AED x · Residential AED y" under each KPI
   const split = rangeData.split;
   const splitNote = (key, fallback) => (split
      ? `Shop ${fmtAED(split.shop[key])} · Residential ${fmtAED(split.residential[key])}`
      : fallback);

   return (
      <div className='mainDiv-all-sideBar'>
         <div className='div-sidebar'>
            <AdminSidebar />
         </div>
         <div className='div-Dashbaord' style={{padding:0}}>
            <div className="dashboard ws-sales-dashboard">
               {loading ? (
                  <SalesSkeleton />
               ) : (
                  <div className="ws-view-block" style={{ padding: 0, }}>

                     {/* Title + filters */}
                     <div className="ws-sec-head" style={{ alignItems: 'flex-start', marginBottom: 16 }}>
                        <div>
                           <div className="ws-view-title">Sales Dashboard</div>
                           <div className="ws-view-sub" style={{ marginBottom: 0 }}>Platform-wide revenue · all providers · net figures after all deductions</div>
                        </div>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                           {['Today', 'This Week', 'This Month', 'All time'].map((r) => (
                              <button key={r} className={`ws-date-pill ${range === r ? 'ws-on' : ''}`} onClick={() => setRange(r)}>{r}</button>
                           ))}
                           <button className="ws-btn-export-top-light" onClick={exportCSV}>↓ Export CSV</button>
                        </div>
                     </div>

                     {/* PLATFORM HEALTH STRIP */}
                     <div className="ws-health-strip">
                        <div className="ws-health-card">
                           <div className="ws-health-icon">👥</div>
                           <div>
                              <div className="ws-health-label">Active Customers</div>
                              <div className="ws-health-val ws-green">{fmtNum(health.activeCustomers)}</div>
                              <div className="ws-health-delta ws-up">{health.activeCustomersDelta}</div>
                           </div>
                        </div>
                        <div className="ws-health-card">
                           <div className="ws-health-icon">⚠️</div>
                           <div>
                              <div className="ws-health-label">At-Risk Customers</div>
                              <div className="ws-health-val ws-amber">{fmtNum(health.atRiskCustomers)}</div>
                              <div className="ws-health-delta">{health.atRiskNote}</div>
                           </div>
                        </div>
                        <div className="ws-health-card">
                           <div className="ws-health-icon">📈</div>
                           <div>
                              <div className="ws-health-label">Completion Rate</div>
                              <div className="ws-health-val ws-purple">{health.completionRate}</div>
                              <div className="ws-health-delta">{health.totalOrders} total orders · {fmtAED(health.totalGMV)}</div>
                           </div>
                        </div>
                        <div className="ws-health-card">
                           <div className="ws-health-icon">📍</div>
                           <div>
                              <div className="ws-health-label">Cancellation Rate</div>
                              <div className="ws-health-val">{health.cancellationRate}</div>
                              <div className="ws-health-delta ws-dn">{health.coverageNote}</div>
                           </div>
                        </div>
                     </div>

                     {/* KPI STRIP — 6 cards */}
                     <div className="ws-kpi-strip ws-kpi-6">
                        <div className="ws-kpi-card ws-gr">
                           <div className="ws-kpi-label">Gross GMV</div>
                           <div className="ws-kpi-val ws-green">{fmtAED(rangeData.kpis.grossGMV)}</div>
                           <div className="ws-kpi-delta ws-up">{splitNote('gmv', 'Shop + residential')}</div>
                        </div>
                        <div className="ws-kpi-card ws-hl">
                           <div className="ws-kpi-label">Commission Earned</div>
                           <div className="ws-kpi-val ws-purple">{fmtAED(rangeData.kpis.commissionEarned)}</div>
                           <div className="ws-kpi-delta ws-lv">{splitNote('commission', 'Washta income · API-derived')}</div>
                        </div>
                        <div className="ws-kpi-card ws-gr">
                           <div className="ws-kpi-label">Platform Fees</div>
                           <div className="ws-kpi-val ws-green">{fmtAED(rangeData.kpis.platformFees)}</div>
                           <div className="ws-kpi-delta ws-up">{splitNote('platformFee', 'Washta income · API-derived')}</div>
                        </div>
                        <div className="ws-kpi-card ws-rd">
                           <div className="ws-kpi-label">Stripe Fees</div>
                           <div className="ws-kpi-val ws-red">− {fmtAED(rangeData.kpis.stripeFees)}</div>
                           <div className="ws-kpi-delta">{splitNote('stripeFee', 'Processing cost from API')}</div>
                        </div>
                        <div className="ws-kpi-card ws-am">
                           <div className="ws-kpi-label">Promo Discounts</div>
                           <div className="ws-kpi-val ws-amber">− {fmtAED(rangeData.kpis.promoDiscounts)}</div>
                           <div className="ws-kpi-delta">Shop orders only · packages have no promo</div>
                        </div>
                        <div className="ws-kpi-card ws-dk">
                           <div className="ws-kpi-label">Washta Net</div>
                           <div className="ws-kpi-val ws-white" style={negStyle(rangeData.kpis.washtaNet) && { color: '#ff6b6b' }}>{fmtAED(rangeData.kpis.washtaNet)}</div>
                           <div className="ws-kpi-delta ws-lv">{splitNote('washtaNet', 'Net revenue from API')}</div>
                        </div>
                     </div>

                     {/* REVENUE FORMULA BOX */}
                     <div className="ws-formula-box">
                        <div className="ws-formula-title">Revenue formula per transaction — values below follow the selected API range</div>
                        <div className="ws-f-row"><span className="ws-f-tag ws-ft-gross">GROSS GMV</span>Shop price (service + add-ons) before promo + residential package price<span className="ws-f-eq">{fmtAED(rangeData.kpis.grossGMV)}</span></div>
                        <div className="ws-f-row"><span className="ws-f-tag ws-ft-disc">PROMO</span>Promo discounts absorbed by Washta in the selected period · deducted from Washta net<span className="ws-f-eq">− {fmtAED(rangeData.kpis.promoDiscounts)}</span></div>
                        <div className="ws-f-row"><span className="ws-f-tag ws-ft-fee">PLATFORM FEE</span>Customer platform fee (5% of discounted / package price, min 1.50, max 5.00)<span className="ws-f-eq">+ {fmtAED(rangeData.kpis.platformFees)}</span></div>
                        <div className="ws-f-row"><span className="ws-f-tag ws-ft-stripe">STRIPE FEE</span>Processing cost on charged amount<span className="ws-f-eq">− {fmtAED(rangeData.kpis.stripeFees)}</span></div>
                        <div className="ws-f-row"><span className="ws-f-tag ws-ft-comm">COMMISSION</span>Commission earned by Washta (8% of shop / package price)<span className="ws-f-eq">+ {fmtAED(rangeData.kpis.commissionEarned)}</span></div>
                        <div className="ws-f-row"><span className="ws-f-tag ws-ft-liab">PROVIDER NET</span>Provider entitlement (shop / package price − commission)<span className="ws-f-eq">{fmtAED(rangeData.platformTotal.providerEntitlement)}</span></div>
                        <div className="ws-f-row ws-f-row-total"><span className="ws-f-tag ws-ft-net">WASHTA NET</span>Commission + platform fee − Stripe fee − promo<span className="ws-f-eq ws-f-eq-net" style={negStyle(rangeData.kpis.washtaNet)}>{fmtAED(rangeData.kpis.washtaNet)}</span></div>
                     </div>

                     {/* PROVIDER COMPARISON — SHOP ORDERS */}
                     <div className="ws-sec-head">
                        <div className="ws-sec-title">Provider comparison · Shop orders · GMV this period</div>
                        <button className="ws-btn-ex" onClick={exportCSV}>↓ Export CSV</button>
                     </div>
                     <ProviderGmvChart title="Shop GMV by provider" rows={providerTables.shopRows} />
                     <ProviderBreakdownTable rows={providerTables.shopRows} total={providerTables.shopTotal} totalLabel="Shop Orders Total" />

                     {/* PROVIDER COMPARISON — RESIDENTIAL PACKAGES */}
                     <div className="ws-sec-head" style={{ marginTop: 16 }}>
                        <div className="ws-sec-title">Provider comparison · Residential packages · GMV this period</div>
                     </div>
                     <ProviderGmvChart title="Package GMV by provider" rows={providerTables.residentialRows} />
                     <ProviderBreakdownTable rows={providerTables.residentialRows} total={providerTables.residentialTotal} totalLabel="Packages Total" showPromo={false} />

                     {/* MAIN + RIGHT PANEL */}
                     <div style={{ display: 'flex', gap: 16, marginTop: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                        <div style={{ flex: '1 1 600px', minWidth: 0 }}>
                           {/* Top locations by orders — collapsible */}
                           <div>
                              <div className={`ws-collapsible-head ${topOpen ? 'ws-open' : ''}`} onClick={() => setTopOpen((p) => !p)}>
                                 <div className="ws-collapsible-title">📍 Top Locations by Orders</div>
                                 <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <span className="ws-collapsible-meta">{rangeData.topLocations.length} locations</span>
                                    <span style={{ fontSize: 12, color: '#aaa' }}>{topOpen ? '▲' : '▼'}</span>
                                 </div>
                              </div>
                              {topOpen && (
                                 <div className="ws-collapsible-body">
                                    <table className="ws-promo-tbl">
                                       <thead>
                                          <tr><th>Rank</th><th>Shop</th><th>Location</th><th className="ws-r">Orders</th><th className="ws-r">GMV</th></tr>
                                       </thead>
                                       <tbody>
                                          {rangeData.topLocations.map((r, i) => (
                                             <tr key={i}>
                                                <td><span className={`ws-rank-dot ${rankClass(i)}`}>{i + 1}</span></td>
                                                <td><span className="ws-promo-code-badge">{r.shop}</span></td>
                                                <td>{r.location}<OrderTypeTag type={r.orderType} /></td>
                                                <td className="ws-r">{r.orders}</td>
                                                <td className="ws-r">{fmtAED(r.gmv)}</td>
                                             </tr>
                                          ))}
                                       </tbody>
                                    </table>
                                 </div>
                              )}
                           </div>

                           {/* PROMO CODE PERFORMANCE — collapsible */}
                           <div style={{ marginTop: 16 }}>
                              <div className={`ws-collapsible-head ${promoOpen ? 'ws-open' : ''}`} onClick={() => setPromoOpen((p) => !p)}>
                                 <div className="ws-collapsible-title">
                                    🏷️ Promo Code Performance
                                       <span className="ws-count-badge">{promoSummary.totalCount} code{promoSummary.totalCount === 1 ? '' : 's'}</span>
                                 </div>
                                 <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                       <span className="ws-collapsible-meta">Total usage: {promoSummary.totalUsage} · Total discount: {fmtAED(promoSummary.totalDiscount)}</span>
                                    <span style={{ fontSize: 12, color: '#aaa' }}>{promoOpen ? '▲' : '▼'}</span>
                                 </div>
                              </div>
                              {promoOpen && (
                                 <div className="ws-collapsible-body">
                                    <div className="ws-table-scroll">
                                       <table className="ws-promo-tbl">
                                          <thead>
                                             <tr>
                                                   <th>Code</th><th>Type</th><th className="ws-r">Usage</th><th className="ws-r">Total Discount</th>
                                                   <th className="ws-r">Average Discount</th><th className="ws-r">Total GMV</th>
                                             </tr>
                                          </thead>
                                          <tbody>
                                             {rangeData.promoCodes.map((p, i) => (
                                                <tr key={i}>
                                                   <td><span className="ws-promo-code-badge">{p.code}</span></td>
                                                      <td><span className="ws-pt-platform">{p.type}</span></td>
                                                      <td className="ws-r">{p.usageCount}</td>
                                                      <td className="ws-r">{fmtAED(p.totalDiscount)}</td>
                                                      <td className="ws-r">{fmtAED(p.averageDiscount)}</td>
                                                      <td className="ws-r">{fmtAED(p.totalGMV)}</td>
                                                </tr>
                                             ))}
                                          </tbody>
                                       </table>
                                    </div>
                                       <div className="ws-info-banner">
                                          The promo endpoint returns usage totals and discount value. Weekly redemption curves are not exposed by the backend yet, so the page now shows only live API fields.
                                       </div>
                                 </div>
                              )}
                           </div>
                        </div>

                        {/* RIGHT PANEL — Top Locations + mixes */}
                        <div className="ws-right-panel">
                           <div className="ws-rp-title">Top Locations</div>
                           {rangeData.topLocations.map((r, i) => (
                              <div className="ws-rp-item" key={i}>
                                 <div className="ws-rp-name">
                                    <span className={`ws-rank-dot ${rankClass(i)}`}>{i + 1}</span>{r.shop} · {r.location}
                                 </div>
                                 <div className="ws-rp-stats">
                                    <div><div className="ws-rp-stat-val">{r.orders}</div><div className="ws-rp-stat-label">orders</div></div>
                                    <div><div className="ws-rp-stat-val">{fmtAED(r.gmv)}</div><div className="ws-rp-stat-label">GMV</div></div>
                                 </div>
                              </div>
                           ))}

                           <div className="ws-divider"></div>
                           <div className="ws-rp-title">Service Mix</div>
                           {rangeData.serviceMix.length > 0 ? rangeData.serviceMix.map((s, i) => (
                              <div className="ws-mix-row" key={i}>
                                 <span className="ws-mix-label">{s.name}</span>
                                 <div className="ws-mix-track"><div className="ws-mix-fill" style={{ width: `${s.pct}%` }}></div></div>
                                 <span className="ws-mix-val">{s.pct}%</span>
                              </div>
                           )) : <div className="ws-muted">No service mix data returned by the API.</div>}

                           <div className="ws-divider"></div>
                           <div className="ws-rp-title">Car Type Split</div>
                           {rangeData.carTypeSplit.length > 0 ? rangeData.carTypeSplit.map((s, i) => (
                              <div className="ws-mix-row" key={i}>
                                 <span className="ws-mix-label">{s.name}</span>
                                 <div className="ws-mix-track"><div className="ws-mix-fill" style={{ width: `${s.pct}%`, background: s.color }}></div></div>
                                 <span className="ws-mix-val" style={{ color: s.color }}>{s.pct}%</span>
                              </div>
                           )) : <div className="ws-muted">No car type split data returned by the API.</div>}

                           <div className="ws-divider"></div>
                           <div className="ws-rp-title">Add-on Performance</div>
                           {rangeData.addOns.length > 0 ? rangeData.addOns.map((s, i) => (
                              <div className="ws-mix-row" key={i}>
                                 <span className="ws-mix-label">{s.name}</span>
                                 <div className="ws-mix-track"><div className="ws-mix-fill" style={{ width: `${s.pct}%`, background: '#1a6641' }}></div></div>
                                 <span className="ws-mix-val" style={{ color: '#1a6641' }}>{s.pct}%</span>
                              </div>
                           )) : <div className="ws-muted">No add-on performance data returned by the API.</div>}
                           <div style={{ fontSize: 9, color: '#aaa', marginTop: 4, lineHeight: 1.5 }}>Share of add-on usage within the selected range.</div>

                           <div className="ws-divider"></div>
                           <div style={{ fontSize: 10, color: '#555', lineHeight: 1.5 }}>QB auto-sync: one-way only. Entry No.1 fires on order complete. No data returns from QB to Washta.</div>
                        </div>
                     </div>
                  </div>
               )}
            </div>
         </div>
      </div>
   );
}

export default AdminSales;